import { ChevronUp, ChevronDown, ChevronsUpDown, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { SupplierStatusBadgeV2 } from './SupplierStatusBadgeV2'
import type { SupplierListItem, SupplierSortBy, SupplierSortOrder } from '@/lib/api'
import { useIsMobile } from '../ui/use-mobile'
import { formatRelative } from '@/lib/formatDate'

type Props = {
  suppliers: SupplierListItem[]
  sortBy: SupplierSortBy
  sortOrder: SupplierSortOrder
  onSort: (column: SupplierSortBy) => void
  reloading?: boolean
  onRowClick?: (supplierId: string) => void
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onToggleSelectAll: () => void
}

type SortableColumn = {
  key: Extract<SupplierSortBy, 'name' | 'status' | 'lastActivity'>
  label: string
  className?: string
}

const SORTABLE_COLUMNS: SortableColumn[] = [
  { key: 'name', label: 'Nom' },
  { key: 'status', label: 'Statut' },
  { key: 'lastActivity', label: 'Dernière activité' },
]

function SortHeader({
  column,
  sortBy,
  sortOrder,
  onSort,
}: {
  column: SortableColumn
  sortBy: SupplierSortBy
  sortOrder: SupplierSortOrder
  onSort: (c: SupplierSortBy) => void
}) {
  const active = sortBy === column.key
  return (
    <button
      type="button"
      onClick={() => onSort(column.key)}
      className="inline-flex items-center gap-1 font-medium hover:text-foreground"
      aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {column.label}
      {active ? (
        sortOrder === 'asc' ? (
          <ChevronUp className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        )
      ) : (
        <ChevronsUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />
      )}
    </button>
  )
}

function SupplierRow({
  s,
  selected,
  onRowClick,
  onToggleSelect,
}: {
  s: SupplierListItem
  selected: boolean
  onRowClick?: (id: string) => void
  onToggleSelect: (id: string) => void
}) {
  const needsComplement = s.aggregateStatus === 'NEEDS_COMPLEMENT'
  const clickable = Boolean(onRowClick)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (!onRowClick) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onRowClick(s.id)
    }
  }
  return (
    <TableRow
      className={cn(
        needsComplement && 'bg-amber-50 hover:bg-amber-100/70',
        selected && 'bg-primary/5 border-l-2 border-primary',
        clickable && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
      onClick={clickable ? () => onRowClick?.(s.id) : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
      tabIndex={clickable ? 0 : undefined}
      role={clickable ? 'button' : undefined}
      aria-label={clickable ? `Ouvrir le profil de ${s.name}` : undefined}
    >
      <TableCell
        className="w-10"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelect(s.id)}
          aria-label={`Sélectionner ${s.name}`}
        />
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {s.isOverdue && (
            <span
              className="inline-block h-2 w-2 rounded-full bg-destructive"
              aria-label="En retard sur ≥1 campagne"
              role="img"
            />
          )}
          <span>{s.name}</span>
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">{s.email}</TableCell>
      <TableCell className="text-muted-foreground">{s.company ?? '—'}</TableCell>
      <TableCell>
        <SupplierStatusBadgeV2 status={s.aggregateStatus} />
      </TableCell>
      <TableCell>{s.campaignCount}</TableCell>
      <TableCell className="text-muted-foreground">{formatRelative(s.lastActivityAt)}</TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="icon"
          disabled
          aria-label="Actions (bientôt disponible)"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  )
}

function SupplierCard({
  s,
  selected,
  onRowClick,
  onToggleSelect,
}: {
  s: SupplierListItem
  selected: boolean
  onRowClick?: (id: string) => void
  onToggleSelect: (id: string) => void
}) {
  const needsComplement = s.aggregateStatus === 'NEEDS_COMPLEMENT'
  const clickable = Boolean(onRowClick)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onRowClick) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onRowClick(s.id)
    }
  }
  return (
    <div
      className={cn(
        'relative rounded-lg border p-4 pl-10',
        needsComplement ? 'bg-amber-50 border-amber-200' : 'bg-card',
        selected && 'bg-primary/5 border-l-2 border-primary',
        clickable && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
      onClick={clickable ? () => onRowClick?.(s.id) : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
      tabIndex={clickable ? 0 : undefined}
      role={clickable ? 'button' : undefined}
      aria-label={clickable ? `Ouvrir le profil de ${s.name}` : undefined}
    >
      <div
        className="absolute top-1 left-1 flex h-11 w-11 items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelect(s.id)}
          aria-label={`Sélectionner ${s.name}`}
        />
      </div>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {s.isOverdue && (
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-full bg-destructive"
              aria-label="En retard sur ≥1 campagne"
              role="img"
            />
          )}
          <div className="min-w-0">
            <div className="truncate font-medium">{s.name}</div>
            <div className="truncate text-sm text-muted-foreground">{s.email}</div>
          </div>
        </div>
        <SupplierStatusBadgeV2 status={s.aggregateStatus} />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span className="truncate">{s.company ?? '—'}</span>
        <span>
          {s.campaignCount} campagne{s.campaignCount > 1 ? 's' : ''} · {formatRelative(s.lastActivityAt)}
        </span>
      </div>
    </div>
  )
}

export function SuppliersTable({
  suppliers,
  sortBy,
  sortOrder,
  onSort,
  reloading,
  onRowClick,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: Props) {
  const isMobile = useIsMobile()

  const hasRows = suppliers.length > 0
  const allSelected = hasRows && suppliers.every((s) => selectedIds.has(s.id))
  const someSelected = !allSelected && suppliers.some((s) => selectedIds.has(s.id))
  const headerChecked: boolean | 'indeterminate' = allSelected
    ? true
    : someSelected
      ? 'indeterminate'
      : false

  if (isMobile) {
    return (
      <div
        className={cn('flex flex-col gap-3', reloading && 'opacity-60 pointer-events-none')}
      >
        {suppliers.map((s) => (
          <SupplierCard
            key={s.id}
            s={s}
            selected={selectedIds.has(s.id)}
            onRowClick={onRowClick}
            onToggleSelect={onToggleSelect}
          />
        ))}
      </div>
    )
  }

  return (
    <div className={cn('rounded-lg border', reloading && 'opacity-60 pointer-events-none')}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              {hasRows && (
                <Checkbox
                  checked={headerChecked}
                  onCheckedChange={onToggleSelectAll}
                  aria-label={
                    allSelected
                      ? 'Tout désélectionner sur cette page'
                      : 'Tout sélectionner sur cette page'
                  }
                />
              )}
            </TableHead>
            <TableHead>
              <SortHeader
                column={SORTABLE_COLUMNS[0]}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Entreprise</TableHead>
            <TableHead>
              <SortHeader
                column={SORTABLE_COLUMNS[1]}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>Campagnes</TableHead>
            <TableHead>
              <SortHeader
                column={SORTABLE_COLUMNS[2]}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
            <TableHead aria-label="Actions" className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {suppliers.map((s) => (
            <SupplierRow
              key={s.id}
              s={s}
              selected={selectedIds.has(s.id)}
              onRowClick={onRowClick}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
